(() => {
  'use strict';

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let lastFocused = null;

  const lockBody = (locked) => document.body.classList.toggle('is-locked', locked);

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const elements = [...container.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openLayer = (layer, focusTarget) => {
    if (!layer) return;
    lastFocused = document.activeElement;
    layer.hidden = false;
    lockBody(true);
    requestAnimationFrame(() => (focusTarget || layer.querySelector(focusableSelector))?.focus());
  };

  const closeLayer = (layer) => {
    if (!layer) return;
    layer.hidden = true;
    lockBody(false);
    lastFocused?.focus();
  };

  const initDrawers = (scope = document) => {
    const menu = document.querySelector('[data-menu-drawer]');
    scope.querySelectorAll('[data-menu-open]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        button.setAttribute('aria-expanded', 'true');
        openLayer(menu);
      });
    });
    menu?.querySelectorAll('[data-menu-close]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        document.querySelector('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
        closeLayer(menu);
      });
    });
    if (menu && !menu.dataset.keyBound) {
      menu.dataset.keyBound = 'true';
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeLayer(menu);
        trapFocus(menu, event);
      });
    }

    const searchDialog = document.querySelector('[data-search-modal]');
    scope.querySelectorAll('[data-search-open]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        lastFocused = button;
        searchDialog?.showModal();
        searchDialog?.querySelector('input[type="search"]')?.focus();
      });
    });
    searchDialog?.querySelector('[data-close-search]')?.addEventListener('click', () => searchDialog.close());
    searchDialog?.addEventListener('close', () => lastFocused?.focus(), { once: false });

    const filterDrawer = document.querySelector('[data-filter-drawer]');
    scope.querySelectorAll('[data-filter-open]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        filterDrawer?.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        lockBody(true);
        filterDrawer?.querySelector(focusableSelector)?.focus();
      });
    });
    filterDrawer?.querySelector('[data-filter-close]')?.addEventListener('click', () => {
      filterDrawer.classList.remove('is-open');
      document.querySelector('[data-filter-open]')?.setAttribute('aria-expanded', 'false');
      lockBody(false);
    });
  };

  const initReveal = (scope = document) => {
    const elements = scope.querySelectorAll('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    elements.forEach((element) => observer.observe(element));
  };

  const initQuantityButtons = (scope = document) => {
    scope.querySelectorAll('[data-quantity-minus], [data-quantity-plus]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const wrapper = button.closest('.quantity');
        const input = wrapper?.querySelector('input[type="number"]');
        if (!input) return;
        const step = Number(input.step) || 1;
        const minimum = Number(input.min) || 0;
        const nextValue = Number(input.value || 0) + (button.hasAttribute('data-quantity-plus') ? step : -step);
        input.value = Math.max(minimum, nextValue);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initSlider = (scope = document) => {
    scope.querySelectorAll('[data-slider]').forEach((slider) => {
      if (slider.dataset.initialized) return;
      slider.dataset.initialized = 'true';
      const track = slider.querySelector('[data-slider-track]');
      const slides = [...slider.querySelectorAll('[data-slide]')];
      const dots = [...slider.querySelectorAll('[data-slider-dot]')];
      if (!track || slides.length < 2) return;
      let index = 0;
      let timer;
      const show = (nextIndex) => {
        index = (nextIndex + slides.length) % slides.length;
        track.scrollTo({ left: track.clientWidth * index, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        dots.forEach((dot, dotIndex) => dot.setAttribute('aria-selected', dotIndex === index ? 'true' : 'false'));
      };
      const start = () => {
        clearInterval(timer);
        if (slider.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          timer = setInterval(() => show(index + 1), Number(slider.dataset.speed) || 6000);
        }
      };
      slider.querySelector('[data-slider-prev]')?.addEventListener('click', () => { show(index - 1); start(); });
      slider.querySelector('[data-slider-next]')?.addEventListener('click', () => { show(index + 1); start(); });
      dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.sliderDot)); start(); }));
      slider.addEventListener('mouseenter', () => clearInterval(timer));
      slider.addEventListener('mouseleave', start);
      start();
    });
  };

  const initCountdown = (scope = document) => {
    scope.querySelectorAll('[data-countdown-section]').forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const end = new Date(section.dataset.countdown).getTime();
      if (!Number.isFinite(end)) {
        section.querySelector('[data-countdown-display]')?.setAttribute('hidden', '');
        section.querySelector('[data-countdown-expired]')?.removeAttribute('hidden');
        return;
      }
      const update = () => {
        const difference = end - Date.now();
        if (difference <= 0) {
          section.querySelector('[data-countdown-display]')?.setAttribute('hidden', '');
          section.querySelector('[data-countdown-expired]')?.removeAttribute('hidden');
          return false;
        }
        const units = {
          days: Math.floor(difference / 86400000),
          hours: Math.floor((difference / 3600000) % 24),
          minutes: Math.floor((difference / 60000) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
        Object.entries(units).forEach(([key, value]) => {
          const target = section.querySelector(`[data-${key}]`);
          if (target) target.textContent = String(value).padStart(2, '0');
        });
        return true;
      };
      if (!update()) return;
      const timer = setInterval(() => { if (!update()) clearInterval(timer); }, 1000);
    });
  };

  const initBeforeAfter = (scope = document) => {
    scope.querySelectorAll('[data-before-after]').forEach((element) => {
      const input = element.querySelector('[data-before-after-input]');
      if (!input || input.dataset.bound) return;
      input.dataset.bound = 'true';
      input.addEventListener('input', () => element.style.setProperty('--position', `${input.value}%`));
    });
  };

  const initRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-recommendations]').forEach(async (element) => {
      if (element.dataset.loaded || !element.dataset.url || element.querySelector('.product-card')) return;
      element.dataset.loaded = 'true';
      try {
        const response = await fetch(element.dataset.url);
        if (!response.ok) return;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = documentFragment.querySelector('[data-recommendations]');
        if (replacement?.innerHTML.trim()) {
          element.innerHTML = replacement.innerHTML;
          initReveal(element);
          initQuantityButtons(element);
        }
      } catch (error) {
        console.warn('Product recommendations could not load.', error);
      }
    });
  };

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[character]);
  const money = (cents) => {
    const amount = (Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (window.theme?.moneyFormat || '${{amount}}').replace(/\{\{\s*amount(?:_no_decimals)?\s*\}\}/, amount);
  };

  const initRecentlyViewed = (scope = document) => {
    scope.querySelectorAll('[data-recently-viewed]').forEach(async (section) => {
      if (section.dataset.loaded) return;
      section.dataset.loaded = 'true';
      const key = 'sabbie-recent-products';
      let handles = [];
      try { handles = JSON.parse(localStorage.getItem(key) || '[]'); } catch (_) { handles = []; }
      const current = section.dataset.currentHandle;
      if (current) {
        handles = [current, ...handles.filter((handle) => handle !== current)].slice(0, 12);
        localStorage.setItem(key, JSON.stringify(handles));
      }
      const displayHandles = handles.filter((handle) => handle && handle !== current).slice(0, Number(section.dataset.limit) || 4);
      const grid = section.querySelector('[data-recently-viewed-grid]');
      if (!grid || !displayHandles.length) {
        section.hidden = true;
        return;
      }
      const products = (await Promise.all(displayHandles.map(async (handle) => {
        try {
          const response = await fetch(`${window.theme.rootUrl}products/${encodeURIComponent(handle)}.js`);
          return response.ok ? response.json() : null;
        } catch (_) { return null; }
      }))).filter(Boolean);
      if (!products.length) {
        section.hidden = true;
        return;
      }
      grid.innerHTML = products.map((product) => {
        const image = product.featured_image || product.images?.[0] || '';
        const available = product.available;
        return `<article class="product-card">
          <a class="product-card__media ratio--portrait" href="${window.theme.rootUrl}products/${encodeURIComponent(product.handle)}">
            ${image ? `<img src="${escapeHTML(image)}" loading="lazy" width="600" height="750" alt="${escapeHTML(product.title)}">` : ''}
            ${available ? '' : '<span class="badge badge--sold-out">Sold out</span>'}
          </a>
          <div class="product-card__content">
            <h3 class="product-card__title"><a href="${window.theme.rootUrl}products/${encodeURIComponent(product.handle)}">${escapeHTML(product.title)}</a></h3>
            <div class="price"><span class="price__current">${escapeHTML(money(product.price))}</span></div>
          </div>
        </article>`;
      }).join('');
    });
  };

  const initMisc = (scope = document) => {
    scope.querySelectorAll('[data-localization-select]').forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
    scope.querySelectorAll('[data-sort-select]').forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
    scope.querySelectorAll('[data-copy-link]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(button.dataset.url || location.href);
          const original = button.textContent;
          button.textContent = 'Copied';
          setTimeout(() => { button.textContent = original; }, 1800);
        } catch (_) {
          window.prompt('Copy this link:', button.dataset.url || location.href);
        }
      });
    });
    scope.querySelectorAll('[data-confirm-message]').forEach((button) => {
      if (button.dataset.bound) return;
      button.dataset.bound = 'true';
      button.addEventListener('click', (event) => {
        if (!window.confirm(button.dataset.confirmMessage)) event.preventDefault();
      });
    });
    scope.querySelectorAll('[data-customer-addresses] select[name="address[country]"]').forEach((country) => {
      if (country.dataset.bound) return;
      country.dataset.bound = 'true';
      const province = country.closest('form')?.querySelector('select[name="address[province]"]');
      const update = () => {
        if (!province) return;
        const provinces = JSON.parse(country.options[country.selectedIndex]?.dataset.provinces || '[]');
        province.innerHTML = provinces.map(([value, label]) => `<option value="${escapeHTML(value)}">${escapeHTML(label)}</option>`).join('');
        province.closest('.field')?.toggleAttribute('hidden', provinces.length === 0);
        if (province.dataset.default) province.value = province.dataset.default;
      };
      country.addEventListener('change', update);
      if (country.dataset.default) country.value = country.dataset.default;
      update();
    });
  };

  const initGlobal = (scope = document) => {
    initDrawers(scope);
    initReveal(scope);
    initQuantityButtons(scope);
    initSlider(scope);
    initCountdown(scope);
    initBeforeAfter(scope);
    initRecommendations(scope);
    initRecentlyViewed(scope);
    initMisc(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initGlobal();
    document.querySelectorAll('.header__details').forEach((details) => {
      details.addEventListener('mouseenter', () => { if (matchMedia('(min-width: 1101px)').matches) details.open = true; });
      details.addEventListener('mouseleave', () => { if (matchMedia('(min-width: 1101px)').matches) details.open = false; });
    });

    const backToTop = document.querySelector('[data-back-to-top]');
    if (backToTop) {
      addEventListener('scroll', () => backToTop.classList.toggle('is-visible', scrollY > 500), { passive: true });
      backToTop.addEventListener('click', () => scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
    }

    const cookie = document.querySelector('[data-cookie-notice]');
    if (cookie && localStorage.getItem('sabbie-cookie-accepted') !== 'true') cookie.hidden = false;
    cookie?.querySelector('[data-cookie-accept]')?.addEventListener('click', () => {
      localStorage.setItem('sabbie-cookie-accepted', 'true');
      cookie.hidden = true;
    });

    const popup = document.querySelector('[data-email-popup]');
    if (popup && sessionStorage.getItem('sabbie-popup-dismissed') !== 'true') {
      setTimeout(() => { if (!popup.open) popup.showModal(); }, Number(popup.dataset.delay || 8) * 1000);
      popup.querySelector('[data-popup-close]')?.addEventListener('click', () => {
        sessionStorage.setItem('sabbie-popup-dismissed', 'true');
        popup.close();
      });
    }
  });

  document.addEventListener('shopify:section:load', (event) => initGlobal(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = document.querySelector('[data-menu-drawer]:not([hidden])');
    if (menu) closeLayer(menu);
    document.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
  });

  window.SabbieTheme = { init: initGlobal, openLayer, closeLayer, trapFocus };
})();
